import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import Icon from '@/components/Icon';
import SidebarIcon from '@/components/Icons/SidebarIcon';
import Text from '@/components/Typography/Text';
import WorkRunHeader from '@/components/Coworker/WorkRunHeader';
import { ThemeProvider, useTheme } from '@/context/ThemeContext';
import { DEFAULT_APP_THEME } from '@/constants/config';
import { isWeb } from '@/components/Landing/landingTokens';

const SCREEN_WIDTH = 390;
const SCREEN_HEIGHT = 844;

// A static sample conversation. Keep the header and composer aligned with
// ChatRouteNavigationOptions, ChatInputFloating and WorkThinkingPowerButton.
// No live account, task, microphone or Remote connection is opened here.
function RemoteChatScreen() {
  const { colors } = useTheme();
  return <View style={[styles.screen, { backgroundColor: colors.solid_color_primary }]}>
    <View style={styles.statusBar} aria-hidden>
      <Text weight="medium" style={{ fontSize: 15, color: colors.text_color_primary }}>9:41</Text>
      <View style={[styles.camera, { backgroundColor: colors.solid_color_tertiary }]} />
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
        <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 2 }}>{[5, 8, 11, 14].map(height => <View key={height} style={{ width: 3, height, borderRadius: 1, backgroundColor: colors.text_color_primary }} />)}</View>
        <View style={{ width: 24, height: 12, padding: 2, borderRadius: 3, borderWidth: 1, borderColor: colors.text_color_secondary }}><View style={{ flex: 1, borderRadius: 1, backgroundColor: colors.text_color_primary }} /></View>
      </View>
    </View>

    <View style={[styles.navigation, { borderBottomColor: colors.border_color_secondary }]}>
      <View style={styles.navigationIcon}><SidebarIcon color={colors.text_color_primary} size={18} /></View>
      <Text numberOfLines={1} style={{ flex: 1, fontSize: 18, color: colors.text_color_primary, textAlign: 'center' }}>Launch checklist</Text>
      <View style={styles.navigationIcon}><Icon name="pen-to-square" size={18} color={colors.text_color_primary} /></View>
      <View style={styles.navigationIcon}><Icon name="magnifying-glass" size={18} color={colors.text_color_primary} /></View>
      <View style={styles.navigationIcon}><Icon name="ellipsis" size={19} color={colors.text_color_primary} /></View>
    </View>

    <View style={styles.transcript}>
      <View style={[styles.userMessage, { backgroundColor: colors.solid_color_user_bubble }]}>
        <Text style={{ fontSize: 17, lineHeight: 25, color: colors.text_color_primary }}>Add a launch-day checklist to the brief on my Mac.</Text>
      </View>
      <View style={{ gap: 14 }}>
        <WorkRunHeader status="completed" />
        <Text style={{ fontSize: 17, lineHeight: 26, color: colors.text_color_primary }}>I updated the brief on your Mac with a checklist covering:</Text>
        <View style={{ gap: 9 }}>
          {['Confirming the final assets', 'Scheduling the announcement', 'Checking links before publishing'].map(item => <View key={item} style={{ flexDirection: 'row', gap: 11, alignItems: 'flex-start' }}>
            <Text style={{ fontSize: 17, lineHeight: 25, color: colors.text_color_primary }}>•</Text>
            <Text style={{ flex: 1, fontSize: 17, lineHeight: 25, color: colors.text_color_primary }}>{item}</Text>
          </View>)}
        </View>
        <View style={[styles.fileCard, { backgroundColor: colors.solid_color_secondary, borderColor: colors.border_color_secondary }]}>
          <View style={[styles.fileIcon, { backgroundColor: colors.solid_color_primary }]}><Icon name="file-lines" size={21} color={colors.text_color_primary} /></View>
          <View style={{ flex: 1, gap: 4 }}><Text weight="medium" style={{ fontSize: 15, color: colors.text_color_primary }}>Launch brief.docx</Text><Text style={{ fontSize: 12, color: colors.text_color_secondary }}>Updated document</Text></View>
          <Icon name="arrow-up-right-from-square" size={14} color={colors.text_color_secondary} />
        </View>
      </View>
    </View>

    <View style={[styles.composer, { borderColor: colors.border_color_input, backgroundColor: colors.solid_color_block }]}>
      <Text style={{ paddingHorizontal: 16, paddingTop: 14, paddingBottom: 13, fontSize: 17, lineHeight: 24, color: colors.color_input_placeholder }}>Send a follow-up…</Text>
      <View style={styles.composerControls}>
        <View style={styles.control}><Icon name="plus" size={20} color={colors.text_color_primary} /></View>
        <View style={{ flex: 1 }} />
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}><Icon name="laptop" size={14} color={colors.text_color_secondary} /><Text numberOfLines={1} style={{ maxWidth: 90, fontSize: 13, color: colors.text_color_primary }}>Spring launch</Text><Icon name="chevron-down" size={8} color={colors.text_color_secondary} /></View>
        <View style={[styles.control, { flexDirection: 'row', gap: 4 }]}><Icon name="brain" size={13} color={colors.text_color_secondary} /><Icon name="chevron-down" size={8} color={colors.text_color_tertiary} /></View>
        <View style={styles.control}><Icon name="microphone" size={18} color={colors.text_color_primary} /></View>
        <View style={[styles.send, { backgroundColor: colors.solid_color_tertiary }]}><Icon name="arrow-up" size={16} color={colors.text_color_tertiary} /></View>
      </View>
    </View>
    <Text style={{ color: colors.text_color_secondary, fontSize: 12, textAlign: 'center', marginTop: 8 }}>AI can make mistakes. Check important info.</Text>
    <View style={styles.homeArea}><View style={{ width: 132, height: 5, borderRadius: 3, backgroundColor: colors.text_color_primary }} /></View>
  </View>;
}

export default function MacRemotePreview() {
  const { colors } = useTheme();
  const [screenWidth, setScreenWidth] = useState(SCREEN_WIDTH);
  return <View style={{ width: '100%', alignItems: 'center', gap: 20 }}>
    <View style={[styles.phone, isWeb && { boxShadow: '0 32px 75px rgba(0,0,0,.20), inset 0 0 0 1px rgba(255,255,255,.15)' }]}>
      <View accessibilityRole="image" accessibilityLabel="Illustrative Gab AI mobile Work chat connected to the Spring launch folder on a Mac" style={styles.viewport}
        onLayout={event => setScreenWidth(event.nativeEvent.layout.width)}>
        <View aria-hidden={isWeb ? true : undefined} pointerEvents="none" style={{ width: SCREEN_WIDTH, height: SCREEN_HEIGHT, transformOrigin: 'top left', transform: [{ scale: screenWidth / SCREEN_WIDTH }] }}>
          <ThemeProvider scoped forceTheme={DEFAULT_APP_THEME}><RemoteChatScreen /></ThemeProvider>
        </View>
      </View>
    </View>
    <Text style={{ fontSize: 12, lineHeight: 19, textAlign: 'center', color: colors.text_color_secondary }}>Mobile Work preview · Illustrative conversation</Text>
  </View>;
}

const styles = StyleSheet.create({
  phone: { width: '100%', maxWidth: 440, padding: 8, borderRadius: 55, backgroundColor: '#343434' },
  viewport: { width: '100%', aspectRatio: SCREEN_WIDTH / SCREEN_HEIGHT, borderRadius: 47, backgroundColor: '#000000', overflow: 'hidden' },
  screen: { width: SCREEN_WIDTH, height: SCREEN_HEIGHT },
  statusBar: { height: 59, paddingHorizontal: 25, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  camera: { position: 'absolute', top: 13, left: 144, width: 102, height: 29, borderRadius: 18 },
  navigation: { height: 50, paddingHorizontal: 8, flexDirection: 'row', alignItems: 'center', borderBottomWidth: StyleSheet.hairlineWidth },
  navigationIcon: { width: 34, height: 44, alignItems: 'center', justifyContent: 'center' },
  transcript: { flex: 1, paddingHorizontal: 18, paddingTop: 24, paddingBottom: 20, gap: 22 },
  userMessage: { maxWidth: '88%', alignSelf: 'flex-end', paddingHorizontal: 16, paddingVertical: 13, borderRadius: 20 },
  fileCard: { flexDirection: 'row', alignItems: 'center', gap: 12, borderWidth: 1, borderRadius: 12, padding: 14 },
  fileIcon: { width: 40, height: 40, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  composer: { marginHorizontal: 12, borderWidth: 1, borderRadius: 24, overflow: 'hidden' },
  composerControls: { height: 46, paddingHorizontal: 7, paddingBottom: 6, flexDirection: 'row', gap: 4, alignItems: 'center' },
  control: { minWidth: 32, height: 34, alignItems: 'center', justifyContent: 'center' },
  send: { width: 34, height: 34, borderRadius: 20, alignItems: 'center', justifyContent: 'center' },
  homeArea: { height: 32, alignItems: 'center', justifyContent: 'center' },
});
